import { DeleteModelRequest, EtlJob, FeatureManagementType, Job, ModelState, Permissions, Project, Status, UpsertModel } from "@api/apiClient";
import { useBenchmarkModelTypes } from "@api/benchmarkModelTypes";
import { useDeleteModel, useModelUsageInfo, useUpdateModel } from "@api/models";
import DataGrid from "@components/DataGrid/DataGrid";
import JobProgressIndicator from "@components/JobProgressIndicator/JobProgressIndicator";
import MicroChip from "@components/MicroChip/MicroChip";
import useAppStore from "@hooks/useAppStore";
import useUserPermissionStore from "@hooks/useUserPermissionStore";
import i18n from "@i18n/i18n";
import AltRouteOutlinedIcon from "@mui/icons-material/AltRouteOutlined";
import DeleteIcon from "@mui/icons-material/Delete";
import LaunchIcon from "@mui/icons-material/Launch";
import { IconButton, Tooltip } from "@mui/material";
import Box from "@mui/material/Box";
import { GridActionsCellItem, GridCallbackDetails, GridColDef, GridRowParams, MuiEvent, useGridApiRef } from "@mui/x-data-grid-premium";
import { formatBytes } from "@utils/formatBytes";
import { useConfirm } from "material-ui-confirm";
import { observer } from "mobx-react";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import useResizeObserver from "use-resize-observer";
import { queryClient } from "@hooks/useGlobalState";
import EtlJobReport from "./EtlJobReport";
import JobReport from "./JobReport";
import ModelSplitDialog from "./ModelSplitDialog";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";

interface Data {
  apsItemName?: string;
  description?: string;
  status?: string;
  modelId?: string;
  apsVersion?: number;
  fileSize?: number;
  createdAt?: Date;
  modifiedAt?: Date;
  createdBy?: string;
  percentageAssign?: number;
}

export interface ProjectGridProps {
  jobs: Job[] | null | undefined;
  etlJobs: EtlJob[] | null | undefined;
  project: Project | null;
}

interface ModelTypeCellProps {
  row: any;
  projectId: string | undefined;
  options: any[];
  isOptionsLoading: boolean;
}

/**
 * Builds a full {@link UpsertModel} from the grid row so PUT /api/models matches other flows
 * (e.g. {@link MuiAddModelsForm} on add) — the backend overwrites several APS fields when absent.
 */
function upsertModelPayloadFromGridRow(row: Record<string, unknown>, projectId: string | undefined, modelTypeId: string | null): UpsertModel {
  const apsFileSize = typeof row.apsFileSize === "number" ? row.apsFileSize : 0;
  const apsIsCompositeDesign = typeof row.apsIsCompositeDesign === "boolean" ? row.apsIsCompositeDesign : false;
  const apsVersion = typeof row.apsVersion === "number" ? row.apsVersion : 0;
  return UpsertModel.fromJS({
    projectId,
    description: row.description,
    assetType: row.assetType,
    projectNumber: row.projectNumber,
    phase: row.phase,
    buildingType: row.buildingType,
    totalArea: row.totalArea,
    apsHubId: row.apsHubId,
    apsHubName: row.apsHubName,
    apsProjectId: row.apsProjectId,
    apsProjectName: row.apsProjectName,
    apsItemId: row.apsItemId,
    apsItemName: row.apsItemName,
    apsViewId: row.apsViewId,
    apsViewName: row.apsViewName,
    apsVersionId: row.apsVersionId,
    apsWebView: row.apsWebView,
    apsStorageId: row.apsStorageId,
    apsCreateUserName: row.apsCreateUserName,
    apsLastModifiedUserName: row.apsLastModifiedUserName,
    apsVersion,
    apsIsCompositeDesign,
    apsFileSize,
    apsFileVersion: row.apsFileVersion,
    modelEmissionReductionTarget: row.modelEmissionReductionTarget,
    modelTypeId: modelTypeId === null ? null : modelTypeId,
  });
}

const ModelTypeCell = ({ row, projectId, options, isOptionsLoading }: ModelTypeCellProps) => {
  const updateModelMutation = useUpdateModel(row.modelId, {
    onSuccess: () => {
      if (projectId) queryClient.invalidateQueries({ queryKey: ["project", projectId] });
    },
  });
  const value = row.modelTypeId ?? "";
  const handleChange = (e: any) => {
    e.stopPropagation();
    const newId = e.target.value || null;
    if (newId === (row.modelTypeId ?? null)) return;
    updateModelMutation.mutate(upsertModelPayloadFromGridRow(row as Record<string, unknown>, projectId, newId));
  };
  return (
    <Select
      size="small"
      value={value}
      onChange={handleChange}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      displayEmpty
      disabled={updateModelMutation.isPending || isOptionsLoading}
      sx={{ minWidth: 140 }}
      renderValue={(selected) => {
        if (isOptionsLoading) return i18n.t("project-grid.model-grid.columns.modelTypeLoading");
        const selectedOption = options.find((opt: any) => opt.metadataEntityId === selected);
        return selectedOption?.name ?? "";
      }}
    >
      {options.map((opt: any) => (
        <MenuItem key={opt.metadataEntityId} value={opt.metadataEntityId ?? ""}>
          {opt.name}
        </MenuItem>
      ))}
    </Select>
  );
};

const ProjectGrid = observer((props: ProjectGridProps) => {
  const { id } = useParams();
  const appStore = useAppStore();
  const isBenchmarkCatalogEnabled = appStore.hasFeature(FeatureManagementType.BenchmarkCatalog);
  const { data: benchmarkModelTypes = [], isLoading: isBenchmarkModelTypesLoading } = useBenchmarkModelTypes({
    enabled: isBenchmarkCatalogEnabled && !!id,
  });
  const navigate = useNavigate();
  const apiRef = useGridApiRef();
  const containerRef = useRef(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedData, setSelectedData] = useState("");
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [selectedReportData, setSelectedReportData] = useState<any>(null);
  const [reportType, setReportType] = useState<'etl' | 'job' | null>(null);
  const upStore = useUserPermissionStore();
  const confirm = useConfirm();
  const deleteModelMutation = useDeleteModel();

  const handleAction = (params: any) => {
    setSelectedData(params);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  const handleReportClick = (modelId: string) => {
    // Check for Design Automation jobs first (most recent)
    const relatedJob = props.jobs?.find((job) => job.modelId === modelId);
    
    if (relatedJob) {
      console.log("Opening Design Automation job report for model:", modelId);
      setSelectedReportData(relatedJob);
      setReportType('job');
      setReportDialogOpen(true);
      return;
    }

    // Fall back to ETL jobs if no Design Automation job found
    const relatedEtlJobs = props.etlJobs?.filter((job) => job.arguments?.ModelId === modelId);
    
    if (relatedEtlJobs && relatedEtlJobs.length > 0) {
      console.log("Opening ETL job report for model:", modelId);
      setSelectedReportData(relatedEtlJobs);
      setReportType('etl');
      setReportDialogOpen(true);
    } else {
      console.log("No reports found for model:", modelId);
    }
  };

  const handleCloseReportDialog = () => {
    setReportDialogOpen(false);
    setSelectedReportData(null);
    setReportType(null);
  };

  // Helper function to check if model is a created version (has suffix & version)
  const isCreatedVersion = (model: any) => {
    return model && model.suffix && model.suffix.trim() && model.version;
  };

  // Handle delete model with same logic as MuiModelTab
  const handleDeleteModel = async (modelData: any) => {
    if (!modelData || !isCreatedVersion(modelData) || !id) return;

    // Confirm deletion
    const modelName = modelData?.modelName;
    const { confirmed } = await confirm({
      title: i18n.t("scope-tab.delete-model-dialog.header"),
      description: i18n.t("scope-tab.delete-model-dialog.message"),
      confirmationText: i18n.t("dialog.yes"),
      cancellationText: i18n.t("dialog.cancel"),
      dialogProps: {
        maxWidth: "xs",
      },
      confirmationButtonProps: {
        variant: "contained",
        color: "error",
      },
      cancellationButtonProps: {
        variant: "outlined",
      },
    });

    if (!confirmed) return;

    try {
      const request = new DeleteModelRequest({
        modelId: modelData.modelId!,
        projectId: id,
      });

      await deleteModelMutation.mutateAsync(request);
    } catch (error) {
      console.error("Error deleting model:", error);
    }
  };

  useResizeObserver({
    ref: containerRef,
    onResize: () => {
      setTimeout(() => {
        apiRef.current?.autosizeColumns(autosizeOptions);
      }, 100);
    },
  });
  const autosizeOptions = {
    columns: ["apsItemName"],
    includeOutliers: true,
    includeHeaders: true,
    expand: true,
  };

  const columns: GridColDef[] = [
    {
      field: "apsItemName",
      headerName: i18n.t("project-grid.model-grid.columns.name"),
      flex: 1,
      renderCell: (params: any) => {
        return params.row?.modelName;
      },
    },
    {
      field: "description",
      headerName: i18n.t("project-grid.model-grid.columns.description"),
      flex: 1,
    },
    {
      field: "status",
      headerName: i18n.t("project-grid.model-grid.columns.status"),
      renderCell: (params: any) => {
        return <StatusColumnCell modelId={params.row.modelId} projectId={id} />;
      },
    },
    {
      field: "fileSize",
      headerName: i18n.t("project-grid.model-grid.columns.fileSize"),
      renderCell: (params: any) => {
        if (!params.row.isSplitted) {
          return formatBytes(params.row.apsFileSize);
        } else {
          return "";
        }
      },
    },
    {
      field: "apsVersion",
      headerName: i18n.t("project-grid.model-grid.columns.version"),
      align: "center",
      renderCell: (params: any) => {
        const versionLabel = params.row?.modelVersion;
        return <MicroChip label={versionLabel} />;
      },
    },
    {
      field: "apsCreateUserName",
      headerName: i18n.t("project-grid.model-grid.columns.createdBy"),
      flex: 1,
    },
    {
      field: "createdAt",
      headerName: i18n.t("project-grid.model-grid.columns.createdAt"),
      flex: 1,
      renderCell: (params: any) => {
        return i18n.t("datetime_short", { value: new Date(params.row.createdAt) });
      },
    },
    {
      field: "modifiedAt",
      headerName: i18n.t("project-grid.model-grid.columns.modifiedAt"),
      flex: 1,
      renderCell: (params: any) => {
        return i18n.t("datetime_short", { value: new Date(params.row.modifiedAt) });
      },
    },
    {
      field: "source",
      headerName: i18n.t("project-grid.model-grid.columns.source.header-name"),
      flex: 1,
      renderCell: (params: any) => {
        const dataSources = params.row.dataSource ? params.row.dataSource.split("|").filter((source: string) => source.trim()) : [];
        // Compensate for older files not having 'split' as a data source
        // Can remove once we have a migration script in-place
        if (params.row.isSplitted && !dataSources.includes("split")) {
          dataSources.push("split");
        }
        return (
          <>
            {dataSources.map((dataSource: string, index: number) => (
              <MicroChip
                key={`${params.row.modelId}-${dataSource}-${index}`}
                label={i18n.t(`project-grid.model-grid.columns.source.${dataSource.trim()}`)}
                size="small"
                sx={{ marginLeft: "2px" }}
              />
            ))}
          </>
        );
      },
    },
    ...(isBenchmarkCatalogEnabled
      ? [
          {
            field: "modelType",
            headerName: i18n.t("project-grid.model-grid.columns.modelType"),
            flex: 1,
            renderCell: (params: any) => (
              <div onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
                <ModelTypeCell row={params.row} projectId={id} options={benchmarkModelTypes} isOptionsLoading={isBenchmarkModelTypesLoading} />
              </div>
            ),
          },
        ]
      : []),
    {
      field: "type",
      headerName: i18n.t("project-grid.model-grid.columns.type.header-name"),
      flex: 1,
      renderCell: (params: any) => {
        const dataTypes = params.row.dataType ? params.row.dataType.split("|").filter((type: string) => type.trim()) : [];
        return (
          <>
            {dataTypes.map((dataType: string, index: number) => (
              <MicroChip
                key={`${params.row.modelId}-${dataType}-${index}`}
                label={i18n.t(`project-grid.model-grid.columns.type.${dataType.trim()}`)}
                size="small"
                sx={{ marginLeft: "2px" }}
              />
            ))}
          </>
        );
      },
    },
  ];

  // Always add actions column (for both split and delete functionality)
  columns.push({
    field: "actions",
    type: "actions",
    headerName: i18n.t("document-grid.action"),
    resizable: false,
    width: 120,
    getActions: (params) => {
      const actions = [];

      // Add split action if feature is enabled
      if (appStore.hasFeature(FeatureManagementType.ModelSplit)) {
        actions.push(
          <GridActionsCellItem
            key={`${params.row.modelId}-split`}
            icon={
              <Tooltip title={i18n.t("project-grid.model-grid.actions.split-model")} arrow>
                <AltRouteOutlinedIcon fontSize="small" sx={{ transform: "rotate(90deg)", transition: "transform 0.2s" }} />
              </Tooltip>
            }
            label={i18n.t("project-grid.model-grid.actions.split-model")}
            onClick={() => handleAction(params.row)}
          />,
        );
      }

      // Add delete action - only show if conditions are met
      actions.push(
        <DeleteActionButton
          key={`${params.row.modelId}-delete`}
          modelData={params.row}
          onDelete={() => handleDeleteModel(params.row)}
          isDeleting={deleteModelMutation.isPending}
        />,
      );

      return actions;
    },
  });

  columns.push({
    field: "report",
    headerName: i18n.t("project-grid.model-grid.columns.report"),
    renderHeader: () => "",
    resizable: false,
    width: 50,
    cellClassName: "no-ellipsis",
    renderCell: (params: any) => {
      return <ReportColumnCell modelId={params.row.modelId} projectId={id} onReportClick={handleReportClick} />;
    },
  });

  return (
    <Box ref={containerRef}>
      <DataGrid
        apiRef={apiRef}
        rows={appStore.project?.models || []}
        columns={columns}
        autoHeight
        hideFooter
        initialState={{
          sorting: {
            sortModel: [{ field: "createdAt", sort: "desc" }],
          },
        }}
        getRowId={(row) => row.modelId}
        onRowClick={(_params: GridRowParams, _event: MuiEvent, _details: GridCallbackDetails) => {
          (async () => {
            const model = appStore.project?.models?.find((model) => model.modelId === _params.id) || null;
            appStore.setModel(model);
            navigate(`/projects/${id}/analysis`);
          })();
        }}
        autosizeOptions={autosizeOptions}
      />
      {appStore.hasFeature(FeatureManagementType.ModelSplit) && <ModelSplitDialog open={dialogOpen} onClose={handleCloseDialog} data={selectedData} />}
      {reportType === 'etl' && (
        <EtlJobReport open={reportDialogOpen} onClose={handleCloseReportDialog} processingData={selectedReportData} />
      )}
      {reportType === 'job' && (
        <JobReport open={reportDialogOpen} onClose={handleCloseReportDialog} job={selectedReportData} />
      )}
    </Box>
  );
});

// Component for status column cell that shows job status
interface StatusColumnCellProps {
  modelId: string;
  projectId?: string;
}

const StatusColumnCell = observer((props: StatusColumnCellProps) => {
  const { modelId, projectId } = props;
  const appStore = useAppStore();
  const [, forceUpdate] = useState({});

  // Direct access to observable store - ensures MobX tracks this properly
  const projectJobs = projectId ? appStore.jobStatus.projects[projectId] : undefined;
  const jobData = projectJobs?.[modelId];
  
  const progress = jobData?.progress;
  const jobStatus = jobData?.jobStatus;

  // Also check ETL job status for Excel imports
  const etlJobData = projectId ? appStore.etlJobStatus.projects?.[projectId]?.[modelId] : undefined;
  
  // Get model from project for model state
  const model = appStore.project?.models?.find((x) => x.modelId === modelId);

  // Force re-render when job data changes by updating state
  // This is needed because DataGrid caches rendered cells
  useEffect(() => {
    forceUpdate({});
  }, [
    progress?.progress,
    progress?.timestamp,
    jobStatus?.workItemStatus?.status,
    etlJobData?.job?.status,
    model?.status,
    modelId,
  ]);

  // Determine status to display (priority: design automation > etl > model state)
  let status = "";
  
  // Check design automation job status
  if (jobStatus?.workItemStatus?.status) {
    status = jobStatus.workItemStatus.status;
  }
  // Check ETL job status
  else if (etlJobData?.job?.status) {
    status = etlJobData.job.status;
  }
  // Check model state for all model types
  else if (model?.status) {
    status = getModelStateStatus(model.status);
  }

  //Convert model state to display status for all model types
  function getModelStateStatus(modelState: ModelState | undefined): string {
    switch (modelState) {
      case ModelState.Created:
        return "created";
      case ModelState.Processing:
        return "pending";
      case ModelState.Success:
        return "success";
      case ModelState.ExtractionFailed:
        return "failedInstructions";
      default:
        return "";
    }
  }

  if (!status) {
    return <></>;
  }

  return <>{i18n.t(`design-automation.status.` + status)}</>;
});

// Component for report column cell that shows progress or report button
interface ReportColumnCellProps {
  modelId: string;
  projectId?: string;
  onReportClick: (modelId: string) => void;
}

const ReportColumnCell = observer((props: ReportColumnCellProps) => {
  const { modelId, projectId, onReportClick } = props;
  const appStore = useAppStore();
  const [, forceUpdate] = useState({});

  // Direct access to observable store - ensures MobX tracks this properly
  const projectJobs = projectId ? appStore.jobStatus.projects[projectId] : undefined;
  const jobData = projectJobs?.[modelId];
  
  const progress = jobData?.progress;
  const jobStatus = jobData?.jobStatus;

  // Force re-render when job data changes by updating state
  // This is needed because DataGrid caches rendered cells
  useEffect(() => {
    forceUpdate({});
  }, [progress?.progress, progress?.timestamp, progress?.type, jobStatus?.workItemStatus?.status, modelId]);

  // Show progress indicator if job is in progress
  const isInProgress = progress !== undefined || (jobStatus?.workItemStatus?.status === Status.Inprogress || jobStatus?.workItemStatus?.status === Status.Pending);

  if (isInProgress) {
    return <JobProgressIndicator progress={progress?.progress} progressType={progress?.type} status={jobStatus?.workItemStatus?.status} size={32} />;
  }

  // Show report button when not in progress or no progress data
  return (
    <Tooltip title={i18n.t("project-grid.model-grid.report-button.title")} arrow>
      <IconButton
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          onReportClick(modelId);
        }}
      >
        <LaunchIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );
});

// Separate component for delete action to properly use hooks
interface DeleteActionButtonProps {
  modelData: any;
  onDelete: () => void;
  isDeleting: boolean;
}

const DeleteActionButton = observer((props: DeleteActionButtonProps) => {
  const { modelData, onDelete, isDeleting } = props;
  const upStore = useUserPermissionStore();
  const modelUsageInfo = useModelUsageInfo(modelData.modelId);

  // Helper function to check if model is a created version (same as MuiModelTab)
  const isCreatedVersion = (model: any) => {
    return model && model.suffix && model.suffix.trim() && model.version;
  };

  // Exact same conditions as MuiModelTab delete button
  const shouldShowDelete =
    isCreatedVersion(modelData) && upStore.hasPermission(Permissions.App_Project_Model_Delete) && !(modelUsageInfo.data ? !modelUsageInfo.data.isDeleteOrUpdateEligible : false);

  // Don't render anything if conditions aren't met
  if (!shouldShowDelete) {
    return null;
  }

  return (
    <GridActionsCellItem
      icon={
        <Tooltip
          title={
            modelUsageInfo.data && !modelUsageInfo.data.isDeleteOrUpdateEligible
              ? `Cannot delete: ${modelUsageInfo.data.usageDetails?.join(", ") || "Unknown reason"}`
              : i18n.t("scope-tab.delete-model-button.title")
          }
        >
          <DeleteIcon />
        </Tooltip>
      }
      label={i18n.t("scope-tab.delete-model-button.title")}
      onClick={onDelete}
      disabled={isDeleting}
    />
  );
});

export default ProjectGrid;
